import { useSummaryProjectedFinancialsData } from "../hooks/sections/useSummaryProjectedFinancialsData";
import { useSummaryPostClosingMetricsData } from "../hooks/sections/useSummaryPostClosingMetricsData";
import { MemoTable } from "./MemoTable";
import { PeriodSelector } from "./PeriodSelector";
import { DeltaIndicator } from "./DeltaIndicator";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";
import { projectionsResolutionAtom } from "../metrics.state";

function DeltaCellRenderer(params: ICellRendererParams) {
    const delta = params.value as number | null;
    return <DeltaIndicator delta={delta} />;
}

export function SummaryProjections() {
    const projectedFinancialsResult = useSummaryProjectedFinancialsData();
    const postClosingMetricsResult = useSummaryPostClosingMetricsData();

    const projectedFinancialsColumns = useMemo<ColDef[]>(() => {
        return projectedFinancialsResult.columns.map((col, index) => {
            if (index === 0) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "left",
                    flex: 1.5,
                    minWidth: 200,
                    cellClass: "body-s font-medium",
                };
            }

            if (index === projectedFinancialsResult.columns.length - 1) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "right",
                    width: 140,
                    cellRenderer: DeltaCellRenderer,
                };
            }

            return {
                field: col.field,
                headerName: col.headerName,
                flex: 1,
                minWidth: 120,
            };
        });
    }, [projectedFinancialsResult.columns]);

    const postClosingMetricsColumns = useMemo<ColDef[]>(() => {
        return postClosingMetricsResult.columns.map((col, index) => {
            if (index === 0) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "left",
                    flex: 1.5,
                    minWidth: 200,
                    cellClass: "body-s font-medium",
                };
            }

            if (index === postClosingMetricsResult.columns.length - 1) {
                return {
                    field: col.field,
                    headerName: col.headerName,
                    pinned: "right",
                    width: 140,
                    cellRenderer: DeltaCellRenderer,
                };
            }

            return {
                field: col.field,
                headerName: col.headerName,
                flex: 1,
                minWidth: 120,
            };
        });
    }, [postClosingMetricsResult.columns]);

    return (
        <div className="space-y-8">
            <div id="summary-projected-financials" className="space-y-3">
                <div className="flex items-start justify-between">
                    <div>
                        <h4 className="heading-m mb-2">
                            Summary Projected Financials
                        </h4>
                        <p className="body-s flex items-center gap-1 italic text-gray-400">
                            Data as of Sept 30, 2025
                            <InformationCircleIcon className="size-4 fill-gray-500" />
                        </p>
                    </div>
                    <PeriodSelector
                        resolutionAtom={projectionsResolutionAtom}
                        variant="projection"
                    />
                </div>
                <p className="body-s text-gray-400">
                    Gross Margin, Sales & Marketing Efficiency and Sales &
                    Marketing spend are projected from historical data using
                    &lt;link to knowledge base article&gt;ARIMA&lt;/link&gt;.
                    Revenue is derived by multiplying Sales & Marketing
                    Efficiency by Sales & Marketing Spend. COGS are derived by
                    multiplying Revenue by Gross Margin.
                </p>
                <MemoTable
                    data={projectedFinancialsResult.data}
                    columns={projectedFinancialsColumns}
                />
            </div>

            <div id="summary-post-closing-metrics" className="space-y-3">
                <div className="flex items-start justify-between">
                    <div>
                        <h4 className="heading-m mb-2">
                            Summary Post-Closing Metrics
                        </h4>
                        <p className="body-s flex items-center gap-1 italic text-gray-400">
                            Data as of Target Close Date
                            <InformationCircleIcon className="size-4 fill-gray-500" />
                        </p>
                    </div>
                </div>
                <MemoTable
                    data={postClosingMetricsResult.data}
                    columns={postClosingMetricsColumns}
                />
            </div>
        </div>
    );
}
